import { Trash2 } from "lucide-react";
import {
  Form,
  useCreate,
  useDelete,
  useGetIdentity,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useMemo } from "react";
import type { FieldValues } from "react-hook-form";

import { SaveButton } from "@/components/admin/form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { toDateOnly } from "../../providers/commons/financeSchedule";
import type { FinanceContract } from "../../types";
import { toContractData } from "../financeFormModels";
import { useFinanceErrorHandler } from "../useFinanceErrorHandler";
import { ContractInputs } from "./ContractInputs";

const RESOURCE = "finance_contracts";

export interface ContractDialogProps {
  open: boolean;
  onClose: () => void;
  /** Contract to edit; when absent the dialog creates one */
  contract?: FinanceContract | null;
  /** Initial values of a new contract (e.g. from a won deal) */
  defaults?: Partial<FinanceContract>;
  onCreated?: (contract: FinanceContract) => void;
}

export const ContractDialog = ({
  open,
  onClose,
  contract,
  defaults,
  onCreated,
}: ContractDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const onError = useFinanceErrorHandler();
  const { identity } = useGetIdentity();
  const [create] = useCreate<FinanceContract>();
  const [update] = useUpdate();
  const [deleteOne, { isPending: isDeleting }] = useDelete();

  const record = useMemo(
    () =>
      contract ?? {
        status: "active",
        setup_amount: 0,
        setup_installments: 1,
        monthly_amount: 0,
        billing_day: 10,
        start_date: toDateOnly(new Date()),
        adjustment_index: "none",
        sales_id: identity?.id,
        ...defaults,
      },
    [contract, defaults, identity?.id],
  );

  const handleSubmit = async (values: FieldValues) => {
    const data = toContractData(values, contract);
    if (contract) {
      await update(
        RESOURCE,
        { id: contract.id, data, previousData: contract },
        {
          onSuccess: () => {
            notify("crm.finance.contracts.updated");
            onClose();
          },
          onError,
        },
      );
      return;
    }
    await create(
      RESOURCE,
      { data: { ...data, sales_id: identity?.id } },
      {
        onSuccess: (created) => {
          notify("crm.finance.contracts.created");
          onCreated?.(created);
          onClose();
        },
        onError,
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-h-9/10 overflow-y-auto lg:max-w-2xl">
        {open && (
          <Form
            record={record}
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
          >
            <DialogHeader>
              <DialogTitle>
                {translate(
                  contract
                    ? "crm.finance.contracts.edit"
                    : "crm.finance.contracts.create",
                )}
              </DialogTitle>
            </DialogHeader>
            <ContractInputs />
            <DialogFooter className="w-full gap-2 sm:justify-between">
              {contract ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive"
                  disabled={isDeleting}
                  onClick={() =>
                    deleteOne(
                      RESOURCE,
                      { id: contract.id, previousData: contract },
                      {
                        onSuccess: () => {
                          notify("crm.finance.contracts.deleted");
                          onClose();
                        },
                        onError,
                      },
                    )
                  }
                >
                  <Trash2 />
                  {translate("ra.action.delete")}
                </Button>
              ) : (
                <span />
              )}
              <SaveButton />
            </DialogFooter>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
